import t from '@rcm/i18n-client';
import { LoadingIndicatorOverlay } from '@rcm/react-utils';
import { InvisibleReCaptcha2Button, useCaptchaActionEnabled } from '@rcm/recaptcha-2';
import { Col, Form, InputTextField, Row } from 'forms';
import React, { useCallback, useState } from 'react';
import '../../styles/password-reset-request-email.scss';
import { PASSWORD_RESET } from '../constants';
import requestToken from '../functions/requestToken.client';

interface Props {}

enum OperationStatus {
  NotStarted = 'NOT_STARTED',
  Loading = 'LOADING',
  Done = 'DONE',
  InvalidFormData = 'INVALID_FORM_DATA',
  CannotResetWithGivenData = 'ERROR_CANNOT_RESET_WITH_GIVEN_DATA',
  CommunicationError = 'COMMUNICATION_ERROR',
  ErrorCaptchaFailed = 'ERROR_CAPTCHA_FAILED',
}

const RequestResetEmailForm = (props: Props) => {
  const [identityString, setIdentityString] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [operationStatus, setOperationStatus] = useState(OperationStatus.NotStarted);

  const isCaptchaEnabled = useCaptchaActionEnabled(PASSWORD_RESET);

  const handleSubmit = useCallback(() => {
    if (!identityString || (isCaptchaEnabled && !captcha)) {
      setOperationStatus(OperationStatus.InvalidFormData);
      return;
    }
    if (!identityString) {
      setOperationStatus(OperationStatus.CannotResetWithGivenData);
      return;
    }
    (async () => {
      setOperationStatus(OperationStatus.Loading);
      const requestData = {
        identityString,
        ...(isCaptchaEnabled ? { captcha } : {}),
      };
      try {
        await requestToken(requestData);
      } catch (e) {
        window?.grecaptcha?.reset();
        if (e.response?.data.error === OperationStatus.ErrorCaptchaFailed) {
          setOperationStatus(OperationStatus.ErrorCaptchaFailed);
        } else if (e.response?.status === 422) {
          setOperationStatus(OperationStatus.CannotResetWithGivenData);
          return;

          // FIXME: This is confusing our users!! It's saying a network error
          //        happened even when we're getting a 4xx or 5xx back from the
          //        server and its telling them to check their network
          //        connection. We should only report "communication error" if
          //        there was actually a network failure, not if there is a
          //        response from the server.
        } else {
          setOperationStatus(OperationStatus.CommunicationError);
          return;
        }
      }
      setOperationStatus(OperationStatus.Done);
    })();
  }, [identityString, isCaptchaEnabled, captcha]);

  return (
    <Row>
      <Col xs={10} sm={8} md={6} lg={4}>
        {operationStatus === OperationStatus.CannotResetWithGivenData && (
          <div className="alert alert-warning" role="alert">
            {t(
              'Your password cannot be reset with the information you provided.' +
                ' You can try providing different information.' +
                ' Please call us if you need additional assistance.'
            )}
          </div>
        )}
        {operationStatus === OperationStatus.CommunicationError && (
          <div className="alert alert-warning" role="alert">
            {t('An error occurred. Please ensure you are connected to the internet and try again.')}
          </div>
        )}
        {operationStatus === OperationStatus.Done && (
          <div className="alert alert-success" role="alert">
            {t(
              'You will receive an email shortly that you can use to reset your password.' +
                " Don't forget to check your spam folder."
            )}
          </div>
        )}
        {operationStatus === OperationStatus.InvalidFormData && (
          <div className="alert alert-warning" role="alert">
            {t('Some information was missing or invalid on the form.' + ' Please check the form and try again.')}
          </div>
        )}
        {operationStatus === OperationStatus.ErrorCaptchaFailed && (
          <div className="alert alert-warning">{t('CAPTCHA validation failed.')}</div>
        )}
        <Form onSubmit={handleSubmit} className="password-reset-request-reset-email-form">
          <LoadingIndicatorOverlay overlay="main" loading={operationStatus === OperationStatus.Loading}>
            <InputTextField
              autoFocus
              label={t('Enter your email, username, or RCN')}
              tabIndex={1}
              value={identityString}
              onValueChange={setIdentityString}
            />
            <div className="password-reset-request-reset-email-submit-container">
              <InvisibleReCaptcha2Button
                action={PASSWORD_RESET}
                badge="inline"
                btn="primary"
                onValueChange={setCaptcha}
                tabIndex={1}
                text={t('Reset Password')}
                type="submit"
              />
            </div>
          </LoadingIndicatorOverlay>
        </Form>
      </Col>
    </Row>
  );
};

export default RequestResetEmailForm;
